import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Loader2, X } from 'lucide-react';
import Alert from './Alert';
import Modal from './Modal';
import { useDocumentGenerationPreferences } from './DocumentGenerationPreferences';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import '../styles/GenerationReview.css';

const GenerationReviewContext = createContext(null);
const STORAGE_PREFIX = 'casedb-generation-review:';
const ALLOWED_GENERATION_ENDPOINTS = new Set([
  'https://api.casedb.co/report-fusion.php',
  'https://api.casedb.co/docgen-upload.php',
]);

const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);

const getRows = (payload = {}) => {
  const candidates = [
    payload.rows,
    payload.preflight?.rows,
    payload.preview?.rows,
    payload.manifest?.rows,
  ];
  return candidates.find(Array.isArray) || [];
};

const getSummary = (payload = {}, rows = []) => {
  const supplied = payload.summary
    || payload.preflight?.summary
    || payload.preview?.summary
    || payload.manifest?.summary
    || {};
  const countStatus = (names) => rows.filter(row => names.includes(String(row?.status || '').toLowerCase())).length;

  return {
    occurrences: Number(firstDefined(supplied.occurrences, supplied.total, rows.length)) || 0,
    resolved: Number(firstDefined(supplied.resolved, countStatus(['resolved', 'default_used', 'ready']))) || 0,
    blank: Number(firstDefined(supplied.blank, supplied.missing, countStatus(['blank', 'missing']))) || 0,
    unresolved: Number(firstDefined(supplied.unresolved, supplied.ambiguous, countStatus(['unresolved', 'ambiguous']))) || 0,
  };
};

const getRevision = (payload = {}, rows = []) => {
  const supplied = firstDefined(
    payload.revision,
    payload.preflight_revision,
    payload.manifest_hash,
    payload.preflight?.revision,
    payload.preflight?.manifest_hash,
    payload.preview?.revision,
    payload.manifest?.revision,
  );
  if (supplied !== undefined) return String(supplied);

  // Browser-side drift guard for transitional preflight responses. Final
  // generation never trusts this value as an authorization decision.
  return JSON.stringify(rows.map(row => [
    row.sheet,
    row.cell,
    row.part,
    row.location,
    row.placeholder,
    row.current_value,
    row.value_index,
    row.value_count,
    row.count,
    row.status,
    row.group_id,
  ]));
};

const normalizeManifest = (payload, config) => {
  const rows = getRows(payload);
  const template = payload.template || payload.preflight?.template || payload.preview?.template || {};
  return {
    raw: payload,
    rows,
    summary: getSummary(payload, rows),
    revision: getRevision(payload, rows),
    evaluationContext: firstDefined(
      payload.evaluation_context,
      payload.preflight?.evaluation_context,
      payload.preview?.evaluation_context,
    ),
    engine: payload.engine || config.engine,
    template: {
      name: template.name || config.templateName || 'Document',
      type: String(template.type || config.templateType || '').toLowerCase(),
    },
  };
};

const getRowSourceCandidates = (row = {}) => [
  row.edit_target,
  row.editTarget,
  row.source?.edit_target,
  row.source,
  row.provenance?.edit_target,
  row.provenance,
  row,
].filter(candidate => candidate && typeof candidate === 'object');

const getCandidateValue = (candidates, snakeName, camelName) => firstDefined(
  ...candidates.flatMap(candidate => [candidate[snakeName], candidate[camelName]]),
);

const normalizeEditTarget = (row = {}) => {
  const candidates = getRowSourceCandidates(row);
  const sectionId = Number(firstDefined(getCandidateValue(candidates, 'section_id', 'sectionId'), 0));
  const fieldId = Number(firstDefined(getCandidateValue(candidates, 'field_id', 'fieldId'), 0));
  const groupIdValue = getCandidateValue(candidates, 'group_id', 'groupId');
  const groupId = groupIdValue === undefined || groupIdValue === null || groupIdValue === ''
    ? null
    : Number(groupIdValue);
  const explicitlyDisabled = row.editable === false
    || firstDefined(...candidates.map(candidate => candidate.editable)) === false;

  if (explicitlyDisabled || !sectionId || !fieldId) return null;
  return { sectionId, fieldId, groupId: Number.isFinite(groupId) ? groupId : null };
};

export const getGenerationReviewRowGroup = (row = {}) => {
  const groupId = getCandidateValue(getRowSourceCandidates(row), 'group_id', 'groupId');
  if (groupId === undefined || groupId === null || String(groupId).trim() === '') return '—';
  return String(groupId);
};

export const getGenerationReviewRowCount = (row = {}) => {
  if (row.count !== undefined && row.count !== null && String(row.count).trim() !== '') {
    return String(row.count);
  }

  const valueCount = Number(firstDefined(row.value_count, row.valueCount, row.count_total, 0));
  const suppliedIndex = Number(firstDefined(row.value_index, row.valueIndex, row.count_index, 1));
  if (valueCount <= 1) return '—';

  // Both v2 backends expose one-based indexes. A preformatted `count` above is
  // still authoritative for transitional providers.
  const displayIndex = suppliedIndex;
  return `${Math.max(1, displayIndex)} of ${valueCount}`;
};

export const getGenerationReviewRowValueDisplay = (row = {}) => {
  const valueKeys = ['current_value', 'currentValue', 'display_value', 'value'];
  const selectedKey = valueKeys.find(key => (
    Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined
  ));
  const rawValue = selectedKey ? row[selectedKey] : null;
  const missing = rawValue === null || String(rawValue).trim() === '';

  return {
    displayValue: missing ? 'Not entered' : String(rawValue),
    missing,
  };
};

const getLocation = (row = {}, isSpreadsheet) => {
  if (isSpreadsheet) {
    return {
      primary: firstDefined(row.sheet, row.location_primary, row.location?.sheet, '—'),
      secondary: firstDefined(row.cell, row.location_secondary, row.location?.cell, '—'),
    };
  }

  return {
    primary: firstDefined(row.part, row.location_primary, row.location?.part, 'Document'),
    secondary: firstDefined(row.location_label, row.location_secondary, row.location?.label, row.cell, '—'),
  };
};

const safeStoredConfig = (config, caseId) => {
  const stripValues = (payload = {}) => {
    const next = { ...payload };
    delete next.unsaved_data;
    delete next.user_id;
    return next;
  };

  return {
    caseId: Number(caseId),
    engine: config.engine,
    endpoint: config.endpoint,
    templateName: config.templateName,
    templateType: config.templateType,
    previewPayload: stripValues(config.previewPayload),
    finalizePayload: stripValues(config.finalizePayload),
    directPayload: config.directPayload ? stripValues(config.directPayload) : undefined,
    source: config.source || null,
  };
};

const getDirectGenerationPayload = (config = {}) => {
  const source = config.directPayload || config.finalizePayload || config.previewPayload || {};
  const payload = { ...source };
  delete payload.action;
  delete payload.preflight_revision;
  delete payload.preflight_id;
  delete payload.evaluation_context;
  if (config.engine === 'docgen') {
    delete payload.lead_id;
    // Restored reviews intentionally omit unsaved field values from
    // sessionStorage. DocGen's direct contract still expects the key.
    payload.unsaved_data = {};
  }
  if (config.engine === 'report_fusion') delete payload.user_id;
  return payload;
};

const requestJson = async (endpoint, payload, signal) => {
  if (!ALLOWED_GENERATION_ENDPOINTS.has(endpoint)) {
    throw new Error('This document review endpoint is not allowed.');
  }
  await ensureCaseDbCsrfToken().catch(() => null);
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
    body: JSON.stringify(payload),
    signal,
  });

  const contentType = response.headers.get('content-type') || '';
  const result = contentType.includes('application/json')
    ? await response.json()
    : { success: false, message: await response.text() };

  if (!response.ok || result?.success === false) {
    const error = new Error(result?.message || result?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.payload = result;
    throw error;
  }

  return result;
};

export const useGenerationReview = () => useContext(GenerationReviewContext);

export const GenerationReviewProvider = ({ caseId, onEditTarget, onGenerated, children }) => {
  const { reviewBeforeGeneration } = useDocumentGenerationPreferences();
  const reviewBeforeGenerationRef = useRef(reviewBeforeGeneration);
  reviewBeforeGenerationRef.current = reviewBeforeGeneration;
  const storageKey = `${STORAGE_PREFIX}${caseId}`;
  const [pending, setPending] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [notice, setNotice] = useState(null);
  const [bypassConfirmOpen, setBypassConfirmOpen] = useState(false);
  const pendingRef = useRef(null);
  const manifestRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const abortControllerRef = useRef(null);
  const writeInFlightRef = useRef(false);

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  useEffect(() => {
    manifestRef.current = manifest;
  }, [manifest]);

  useEffect(() => {
    requestSequenceRef.current += 1;
    abortControllerRef.current?.abort?.();
    abortControllerRef.current = null;
    pendingRef.current = null;
    manifestRef.current = null;
    setBusy(false);
    setPending(null);
    setManifest(null);
    setModalOpen(false);
    setBypassConfirmOpen(false);
    setNotice(null);
    setPhase('idle');
    writeInFlightRef.current = false;

    try {
      const stored = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
      if (stored && Number(stored.caseId) === Number(caseId) && stored.endpoint && stored.previewPayload) {
        pendingRef.current = stored;
        setPending(stored);
      }
    } catch (error) {
      window.sessionStorage.removeItem(storageKey);
    }
  }, [caseId, storageKey]);

  const persistPending = useCallback((config) => {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(safeStoredConfig(config, caseId)));
    } catch (error) {
      // Review still works for this page load when sessionStorage is unavailable.
    }
  }, [caseId, storageKey]);

  const cancelReview = useCallback(() => {
    requestSequenceRef.current += 1;
    abortControllerRef.current?.abort?.();
    abortControllerRef.current = null;
    window.sessionStorage.removeItem(storageKey);
    pendingRef.current = null;
    manifestRef.current = null;
    setBusy(false);
    setPhase('idle');
    setPending(null);
    setManifest(null);
    setModalOpen(false);
    setBypassConfirmOpen(false);
    setNotice(null);
    writeInFlightRef.current = false;
  }, [storageKey]);

  const dismissReview = useCallback(() => {
    if (!pendingRef.current || phase === 'finalizing') return;
    // Both v2 backends treat cancellation as read-only, but notifying the
    // endpoint keeps the contract available for future server-side audit state.
    requestJson(pendingRef.current.endpoint, {
      ...(pendingRef.current.finalizePayload || pendingRef.current.previewPayload || {}),
      action: 'cancel',
    }).catch(() => null);
    cancelReview();
  }, [cancelReview, phase]);

  useEffect(() => {
    if (reviewBeforeGeneration || phase === 'finalizing') return;
    const config = pendingRef.current;
    if (!config) return;
    requestJson(config.endpoint, {
      ...(config.finalizePayload || config.previewPayload || {}),
      action: 'cancel',
    }).catch(() => null);
    cancelReview();
  }, [cancelReview, phase, reviewBeforeGeneration]);

  useEffect(() => () => {
    requestSequenceRef.current += 1;
    abortControllerRef.current?.abort?.();
  }, []);

  const loadPreview = useCallback(async (config, { open = true } = {}) => {
    if (!config?.endpoint || !config?.previewPayload) {
      throw new Error('This template does not provide a preview request.');
    }

    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    abortControllerRef.current?.abort?.();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setBypassConfirmOpen(false);
    const existingEvaluationContext = manifestRef.current?.evaluationContext;
    manifestRef.current = null;
    setManifest(null);
    pendingRef.current = config;
    setPending(config);
    persistPending(config);
    setBusy(true);
    setPhase('preview');
    setNotice(null);
    try {
      const previewPayload = existingEvaluationContext
        ? { ...config.previewPayload, evaluation_context: existingEvaluationContext }
        : config.previewPayload;
      const result = await requestJson(config.endpoint, previewPayload, controller.signal);
      if (requestSequenceRef.current !== requestSequence) return null;
      const nextManifest = normalizeManifest(result, config);
      manifestRef.current = nextManifest;
      setManifest(nextManifest);
      if (open) setModalOpen(true);
      return nextManifest;
    } finally {
      if (requestSequenceRef.current === requestSequence) {
        abortControllerRef.current = null;
        setBusy(false);
        setPhase('idle');
      }
    }
  }, [persistPending]);

  const startReview = useCallback(async (config) => {
    if (!reviewBeforeGenerationRef.current) return null;
    try {
      return await loadPreview(config, { open: true });
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      if (!manifestRef.current) {
        window.sessionStorage.removeItem(storageKey);
        pendingRef.current = null;
        setPending(null);
        setBypassConfirmOpen(false);
      }
      setNotice({
        type: 'error',
        title: 'Could not review document data',
        message: error.message || 'Please try again.',
      });
      error.generationReviewHandled = true;
      throw error;
    }
  }, [loadPreview, storageKey]);

  const openReview = useCallback(async () => {
    const config = pendingRef.current;
    if (!config) return;
    try {
      await loadPreview(config, { open: true });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      setNotice({
        type: 'error',
        title: 'Could not refresh document data',
        message: error.message || 'Please try again.',
      });
    }
  }, [loadPreview]);

  const completeGeneration = useCallback((result, config) => {
    try {
      config.onGenerated?.(result);
    } catch (error) {
      console.error('Document generation callback failed:', error);
    }
    try {
      onGenerated?.(result, config);
    } catch (error) {
      console.error('Document generation refresh callback failed:', error);
    }
    window.dispatchEvent(new CustomEvent('casedb:generation-complete', {
      detail: { result, source: config.source, engine: config.engine },
    }));
    writeInFlightRef.current = false;
    cancelReview();
    setNotice({
      type: 'success',
      title: 'Document generated',
      message: result?.filename || result?.file?.filename || result?.file?.file_name || config.templateName,
    });
  }, [cancelReview, onGenerated]);

  const finalize = useCallback(async () => {
    const config = pendingRef.current;
    const reviewedManifest = manifestRef.current;
    if (!config || !reviewedManifest || writeInFlightRef.current) return;
    writeInFlightRef.current = true;

    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    abortControllerRef.current?.abort?.();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setBusy(true);
    setPhase('finalizing');
    setNotice(null);
    try {
      const refreshedResult = await requestJson(config.endpoint, {
        ...config.previewPayload,
        ...(reviewedManifest.evaluationContext
          ? { evaluation_context: reviewedManifest.evaluationContext }
          : {}),
      }, controller.signal);
      if (requestSequenceRef.current !== requestSequence) return;
      const refreshedManifest = normalizeManifest(refreshedResult, config);
      if (refreshedManifest.revision !== reviewedManifest.revision) {
        writeInFlightRef.current = false;
        manifestRef.current = refreshedManifest;
        setManifest(refreshedManifest);
        setModalOpen(true);
        setNotice({
          type: 'warning',
          title: 'Document data changed',
          message: 'The latest values are now shown. Review them before generating.',
        });
        return;
      }

      const finalizePayload = {
        ...(config.finalizePayload || {}),
        action: config.finalizePayload?.action || 'finalize',
        preflight_revision: refreshedManifest.revision,
        evaluation_context: refreshedManifest.evaluationContext,
        preflight_id: firstDefined(
          refreshedResult.preflight_id,
          refreshedResult.preflight?.id,
          refreshedResult.preview?.id,
        ),
      };
      if (finalizePayload.evaluation_context === undefined) delete finalizePayload.evaluation_context;
      if (finalizePayload.preflight_id === undefined) delete finalizePayload.preflight_id;

      const result = await requestJson(config.endpoint, finalizePayload, controller.signal);
      if (requestSequenceRef.current !== requestSequence) return;
      // The final write has completed. Clear the active request before local
      // cleanup so cancellation cannot misrepresent an in-flight generation.
      abortControllerRef.current = null;
      completeGeneration(result, config);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      const refreshedRows = getRows(error.payload || {});
      if (error.status === 409 && refreshedRows.length > 0) {
        writeInFlightRef.current = false;
        const staleManifest = normalizeManifest(error.payload, config);
        manifestRef.current = staleManifest;
        setManifest(staleManifest);
        setModalOpen(true);
        setNotice({
          type: 'warning',
          title: 'Document data changed',
          message: 'The latest values are now shown. Review them before generating.',
        });
      } else {
        writeInFlightRef.current = false;
        setNotice({
          type: 'error',
          title: 'Document could not be generated',
          message: error.message || 'Please try again.',
        });
      }
    } finally {
      if (requestSequenceRef.current === requestSequence) {
        abortControllerRef.current = null;
        setBusy(false);
        setPhase('idle');
        writeInFlightRef.current = false;
      }
    }
  }, [completeGeneration]);

  const matchesPendingReview = useCallback((expected = {}) => {
    const config = pendingRef.current;
    if (!config) return false;
    const sourceFieldId = firstDefined(
      config.source?.fieldId,
      config.source?.field_id,
      config.directPayload?.field_id,
      config.previewPayload?.field_id,
    );
    if (expected.engine && String(config.engine) !== String(expected.engine)) return false;
    if (expected.templateName && String(config.templateName) !== String(expected.templateName)) return false;
    if (expected.fieldId !== undefined && expected.fieldId !== null
      && Number(sourceFieldId) !== Number(expected.fieldId)) return false;
    return true;
  }, []);

  const requestBypassConfirmation = useCallback((expected = {}) => {
    if (!matchesPendingReview(expected) || phase === 'finalizing' || writeInFlightRef.current) return false;
    setBypassConfirmOpen(true);
    return true;
  }, [matchesPendingReview, phase]);

  const generateWithoutVerification = useCallback(async () => {
    const config = pendingRef.current;
    if (!config || writeInFlightRef.current) return;

    // This synchronous guard closes the gap before React applies the disabled
    // state, preventing a rapid double click from creating two documents.
    writeInFlightRef.current = true;
    setBypassConfirmOpen(false);
    setModalOpen(false);

    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    abortControllerRef.current?.abort?.();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setBusy(true);
    setPhase('finalizing');
    setNotice(null);

    try {
      const result = await requestJson(
        config.endpoint,
        getDirectGenerationPayload(config),
        controller.signal,
      );
      if (requestSequenceRef.current !== requestSequence) return;
      if (config.engine === 'report_fusion') {
        await requestJson(config.endpoint, {
          ...getDirectGenerationPayload(config),
          action: 'cancel',
        }, controller.signal).catch(() => null);
        if (requestSequenceRef.current !== requestSequence) return;
      }
      abortControllerRef.current = null;
      completeGeneration(result, config);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (requestSequenceRef.current !== requestSequence) return;
      setModalOpen(Boolean(manifestRef.current));
      setNotice({
        type: 'error',
        title: 'Document could not be generated',
        message: error.message || 'Please try again.',
      });
    } finally {
      if (requestSequenceRef.current === requestSequence) {
        abortControllerRef.current = null;
        writeInFlightRef.current = false;
        setBusy(false);
        setPhase('idle');
      }
    }
  }, [completeGeneration]);

  const handleRowClick = useCallback((row) => {
    const target = normalizeEditTarget(row);
    if (!target) return;
    const navigated = onEditTarget?.({ ...target, token: Date.now() });
    if (navigated !== false) setModalOpen(false);
  }, [onEditTarget]);

  const contextValue = useMemo(() => ({
    enabled: reviewBeforeGeneration,
    isEnabled: () => reviewBeforeGenerationRef.current,
    active: Boolean(pending),
    busy,
    startReview,
    openReview,
    cancelReview,
    matchesPendingReview,
    requestDirectGeneration: requestBypassConfirmation,
  }), [busy, cancelReview, matchesPendingReview, openReview, pending, requestBypassConfirmation, reviewBeforeGeneration, startReview]);

  const isSpreadsheet = useMemo(() => {
    const type = manifest?.template?.type || pending?.templateType || '';
    const normalizedType = String(type).replace(/^\./, '').toLowerCase();
    if (normalizedType) {
      return ['xlsx', 'xls', 'csv', 'spreadsheet'].includes(normalizedType);
    }
    return String(manifest?.engine || pending?.engine || '').toLowerCase() !== 'docgen'
      && manifest?.rows?.some(row => row.sheet && !row.part);
  }, [manifest, pending]);

  const summary = manifest?.summary || {};
  const attentionCount = Number(summary.blank || 0) + Number(summary.unresolved || 0);
  const alertMessage = manifest
    ? `${summary.resolved || 0} ready${attentionCount ? ` · ${attentionCount} need attention` : ''}`
    : 'Open to refresh and review the values.';

  return (
    <GenerationReviewContext.Provider value={contextValue}>
      {children}

      {pending && (
        <Alert
          className="generation-review-alert"
          type={notice?.type || (attentionCount ? 'warning' : 'notifications')}
          title={notice?.title || `Reviewing ${manifest?.template?.name || pending.templateName || 'document'}`}
          message={phase === 'finalizing'
            ? 'Generating document…'
            : (busy ? 'Refreshing document data…' : (notice?.message || alertMessage))}
          duration={0}
          dismissOnClick={false}
          showProgress={false}
          showClose={phase !== 'finalizing' && !bypassConfirmOpen}
          actionLabel="Open"
          onAction={busy || bypassConfirmOpen || modalOpen ? undefined : openReview}
          secondaryActionLabel={busy ? 'Generate' : 'Generate now'}
          onSecondaryAction={phase === 'finalizing' || bypassConfirmOpen ? undefined : requestBypassConfirmation}
          onClose={phase === 'finalizing' || bypassConfirmOpen ? undefined : dismissReview}
        />
      )}

      {notice && !pending && (
        <Alert
          type={notice.type}
          title={notice.title}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}

      {modalOpen && manifest && !bypassConfirmOpen && (
        <Modal
          title={`Review ${manifest.template.name}`}
          instructions="These are the saved CaseDB values that will be used. Save any edits first, then select Open to refresh this list."
          wider
          zIndex={6000}
          onClose={() => setModalOpen(false)}
          header={(
            <button
              type="button"
              className="modal-close generation-review-close"
              onClick={() => setModalOpen(false)}
              aria-label="Close review"
              title="Close review"
            >
              <X size={18} />
            </button>
          )}
          subheader={(
            <div className="generation-review-summary" aria-live="polite">
              <span>{summary.occurrences || manifest.rows.length} values</span>
              <span>{summary.resolved || 0} ready</span>
              {Number(summary.blank || 0) > 0 && <span className="warning">{summary.blank} blank</span>}
              {Number(summary.unresolved || 0) > 0 && <span className="error">{summary.unresolved} unresolved</span>}
            </div>
          )}
          footer={(
            <div className="modal-footer-actions generation-review-actions">
              <button type="button" className="action alt" onClick={dismissReview} disabled={busy}>
                Cancel generation
              </button>
              <button type="button" className="action" onClick={finalize} disabled={busy}>
                {busy ? <><Loader2 size={16} className="spin" /> Checking values…</> : 'Generate'}
              </button>
            </div>
          )}
        >
          <div className="modal-content-wrapper generation-review-modal-content">
            {notice && (
              <div className={`generation-review-inline-notice ${notice.type}`}>
                <strong>{notice.title}</strong>
                <span>{notice.message}</span>
              </div>
            )}
            <div className="generation-review-table-wrap">
              <table className="generation-review-table">
                <colgroup>
                  <col className="generation-review-col-primary" />
                  <col className="generation-review-col-secondary" />
                  <col className="generation-review-col-placeholder" />
                  <col className="generation-review-col-value" />
                  <col className="generation-review-col-group" />
                  <col className="generation-review-col-count" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{isSpreadsheet ? 'Sheet' : 'Part'}</th>
                    <th>{isSpreadsheet ? 'Cell' : 'Location'}</th>
                    <th>Placeholder</th>
                    <th>Current Value</th>
                    <th>Group</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {manifest.rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="generation-review-empty">No placeholders were found in this template.</td>
                    </tr>
                  ) : manifest.rows.map((row, index) => {
                    const target = normalizeEditTarget(row);
                    const location = getLocation(row, isSpreadsheet);
                    const status = String(row.status || 'resolved').toLowerCase();
                    const { displayValue, missing: valueMissing } = getGenerationReviewRowValueDisplay(row);
                    const rowKey = row.occurrence_id || row.id || `${location.primary}-${location.secondary}-${row.placeholder}-${index}`;
                    return (
                      <tr
                        key={rowKey}
                        className={`${target ? 'is-editable' : 'is-readonly'} status-${status}`}
                        onClick={() => target && handleRowClick(row)}
                        onKeyDown={(event) => {
                          if (target && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            handleRowClick(row);
                          }
                        }}
                        tabIndex={target ? 0 : undefined}
                        title={target ? 'Open this value in the case' : (row.explanation || 'This value is calculated or not directly editable')}
                      >
                        <td>{String(location.primary)}</td>
                        <td>{String(location.secondary)}</td>
                        <td><code>{row.placeholder || row.raw_placeholder || '—'}</code></td>
                        <td className={valueMissing ? 'is-missing' : ''}>{displayValue}</td>
                        <td>{getGenerationReviewRowGroup(row)}</td>
                        <td>{getGenerationReviewRowCount(row)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}

      {bypassConfirmOpen && pending && (
        <Modal
          title="Generate without verification?"
          instructions="Are you sure you want to generate without verifying case data?"
          zIndex={6200}
          onClose={() => setBypassConfirmOpen(false)}
          footer={(
            <div className="modal-footer-actions generation-review-actions">
              <button
                type="button"
                className="action alt"
                onClick={() => setBypassConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="action"
                onClick={generateWithoutVerification}
              >
                Generate without verifying
              </button>
            </div>
          )}
        >
          <div className='modal-content-wrapper'>
            <p className="generation-review-bypass-copy">
              CaseDB will use the case values available when the document is generated. Changes will not be shown for review first.
            </p>
          </div>
        </Modal>
      )}
    </GenerationReviewContext.Provider>
  );
};

export default GenerationReviewProvider;
